import { motion } from "framer-motion";
import { 
  Mic, Video, Image, MessageSquare, Clock, TrendingUp
} from "lucide-react";
import { Badge } from "../ui/badge";

const FEATURES = [
  {
    icon: Mic,
    title: "Voice Commands in 12 Languages",
    description: "Speak naturally in English, Arabic, Spanish, Chinese, French, German, and more. Our AI understands context and intent.",
    iconColor: "#22d3ee",
    bgGradient: "linear-gradient(135deg, rgba(34, 211, 238, 0.2) 0%, rgba(6, 182, 212, 0.2) 100%)"
  },
  {
    icon: Video,
    title: "AI Video Generation",
    description: "Create professional marketing videos with Sora 2. From product demos to social clips – fully automated.",
    iconColor: "#a855f7",
    bgGradient: "linear-gradient(135deg, rgba(168, 85, 247, 0.2) 0%, rgba(236, 72, 153, 0.2) 100%)"
  },
  {
    icon: Image,
    title: "Visual Content Creation",
    description: "Generate stunning images and graphics tailored for each platform using GPT Image 1.5.",
    iconColor: "#f97316",
    bgGradient: "linear-gradient(135deg, rgba(249, 115, 22, 0.2) 0%, rgba(239, 68, 68, 0.2) 100%)"
  },
  {
    icon: MessageSquare,
    title: "Smart Script Writing",
    description: "Platform-optimized scripts that capture your brand voice. LinkedIn professional, TikTok trendy – automatically.",
    iconColor: "#22d3ee",
    bgGradient: "linear-gradient(135deg, rgba(34, 211, 238, 0.2) 0%, rgba(59, 130, 246, 0.2) 100%)"
  },
  {
    icon: Clock,
    title: "Intelligent Scheduling",
    description: "AI determines the best posting times for maximum engagement on each platform.",
    iconColor: "#22c55e",
    bgGradient: "linear-gradient(135deg, rgba(34, 197, 94, 0.2) 0%, rgba(16, 185, 129, 0.2) 100%)"
  },
  {
    icon: TrendingUp,
    title: "Performance Analytics",
    description: "Track campaign performance across all platforms. AI learns and optimizes future content.",
    iconColor: "#f97316",
    bgGradient: "linear-gradient(135deg, rgba(249, 115, 22, 0.2) 0%, rgba(234, 179, 8, 0.2) 100%)"
  }
];

/**
 * FeaturesSection Component - NO ITALICS
 */
export default function FeaturesSection() {
  return (
    <section id="features" className="py-16 sm:py-24 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12 sm:mb-16"
        >
          <Badge className="mb-4 bg-primary/10 text-primary border-primary/20">
            Features
          </Badge>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tighter mb-4" data-testid="features-title">
            Everything You Need, <span className="gradient-text">Nothing You Don't</span>
          </h2>
          <p className="text-muted-foreground max-w-2xl mx-auto text-base sm:text-lg">
            A complete marketing automation suite powered by the latest AI models
          </p>
        </motion.div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
          {FEATURES.map((feature, index) => (
            <motion.div
              key={feature.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className="glass-card rounded-2xl p-5 sm:p-6 border border-white/5 hover:border-primary/30 transition-all card-hover"
              data-testid={`feature-${index}`}
            >
              <div 
                className="w-12 h-12 sm:w-14 sm:h-14 rounded-2xl mb-4 sm:mb-5 flex items-center justify-center"
                style={{ background: feature.bgGradient }}
              >
                <feature.icon 
                  className="w-6 h-6 sm:w-7 sm:h-7" 
                  style={{ color: feature.iconColor }}
                />
              </div>
              <h3 className="text-lg sm:text-xl font-semibold mb-2 sm:mb-3 tracking-tight">{feature.title}</h3>
              <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">{feature.description}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
